import {HeaderItem, TableHeaderAction} from "@src/Pages/Views/tableTypes.ts";
import styles from "./eventsTable.module.less";
import React, {useMemo, useState} from "react";
import {getByPath} from "@src/utils/getByPath.ts";
import {DataNotFoundMessage, WorkingTime} from "@src/Pages/Views/Common.tsx";
import {EmployeeWithWorkdays, WorkDayWithEvents} from "@src/Store/employeesStore.ts";
import {Body_14_Coral_Primary_Regular_14} from "@UI/typography/generated/typography.tsx";
import {Button, ButtonType} from "@UI/components/controls/Button/Button.tsx";
import {ColorNames} from "@UI/theme/generated/ColorNames.ts";
import {ArrowChevronUp} from "@UI/components/icons/ArrowChevronUp.tsx";
import {ArrowChevronDown} from "@UI/components/icons/ArrowChevronDown.tsx";
import cn from "classnames";
import {Gt, GtDatetime} from "@src/i18n/Gt.tsx";
import {Stack} from "@src/CommonComponents/Stack/Stack.tsx";
import {getLocationLink} from "@src/utils/coordLink.ts";
import {CommonEventType, makeGroupEventsOffline} from "@src/Store/eventsStore.ts";
import {getTimeFromDT} from "@src/utils/formatDate.ts";


const TableHeader = ({headerColumns, expandable = false}: {
    headerColumns: Array<HeaderItem>,
    expandable: boolean
}) => {
    return (
        <div className={cn(styles.tableHeader, {[styles.expandable]: expandable})}>
            {headerColumns.map(column =>
                <div className={styles.tableCell} key={column.dataLabel}>
                    <Body_14_Coral_Primary_Regular_14>
                        {column.label}
                    </Body_14_Coral_Primary_Regular_14>
                </div>)
            }
            {expandable && <div className={styles.tableCell} aria-hidden="true"/>}
        </div>
    )
}

const TableRowValue = ({column, item}: { column: HeaderItem, item: EmployeeWithWorkdays }) => {
    const value = useMemo(() => {
        let _value = ""
        if (column.paramKey === "self" && column?.renderer) {
            return column.renderer(item)
        }
        if (column.paramKeys) {
            for (const key of column.paramKeys) {
                _value += getByPath(item, key)
            }
        } else if (column.paramKey) {
            _value = getByPath(item, column.paramKey)
        }

        if (column?.renderer) {
            return column.renderer(_value)
        }
        return _value
    }, [column, item])

    if (!item) return

    return (
        <div className={styles.tableCell} data-label={column.dataLabel}>{value}</div>
    )
}

const LocationLink = ({event}: { event: CommonEventType }) => {
    const value = useMemo(() => {
        if (!event?.latitude || !event?.longitude) return undefined
        if (event?.branch_name) return (
            <Stack direction={"column"} alignItems={"flex-start"}>
                <Body_14_Coral_Primary_Regular_14>
                    {event.branch_name}
                </Body_14_Coral_Primary_Regular_14>
            </Stack>
        )
        return (
            <Stack direction={"column"} alignItems={"flex-start"}>
                <Body_14_Coral_Primary_Regular_14>
                    <Gt>latitude</Gt>: {event?.latitude?.toFixed(4)}
                </Body_14_Coral_Primary_Regular_14>
                <Body_14_Coral_Primary_Regular_14>
                    <Gt>longitude</Gt>: {event?.longitude?.toFixed(4)}
                </Body_14_Coral_Primary_Regular_14>
            </Stack>
        )
    }, [event])

    const link = useMemo(() => {
        return getLocationLink(event)
    }, [event])

    if (!value || !event) return (
        <Body_14_Coral_Primary_Regular_14>
            —:—
        </Body_14_Coral_Primary_Regular_14>
    )

    return (
        <Button type={ButtonType.link}
                className={styles.coordsButton}
                color={ColorNames.CORAL_INK}
                externalLink={true}
                href={link}>
            {value}
        </Button>
    )
}


const WorkCoords = ({workday, type}: { workday: WorkDayWithEvents, type: "in" | "out" }) => {
    const events = useMemo(() => {
        if (!workday) return []
        return makeGroupEventsOffline(workday?.events ?? [])
    }, [workday])

    const noData = (
        <Body_14_Coral_Primary_Regular_14>
            —:—
        </Body_14_Coral_Primary_Regular_14>
    )
    if (!events || events?.length === 0) return noData
    let event = type === "in" ? events[0]?.event_in : events[events.length - 1]?.event_out;
    if (!event) return noData
    return <LocationLink event={event}/>
}

const renderCoords = (workday: WorkDayWithEvents, type: "in" | "out") => {
    return <WorkCoords workday={workday} type={type}/>
}


const WorkTime = ({workday, type}: { workday: WorkDayWithEvents, type: "in" | "out" }) => {
    const events = useMemo(() => {
        if (!workday) return []
        return makeGroupEventsOffline(workday?.events ?? [])
    }, [workday])

    const noData = (
        <Body_14_Coral_Primary_Regular_14>
            —
        </Body_14_Coral_Primary_Regular_14>
    )
    if (!events || events?.length === 0) return noData
    const event = type === "in" ? events[0]?.event_in : events[events.length - 1]?.event_out;
    if (!event) return noData
    return (
        <Body_14_Coral_Primary_Regular_14>
            {getTimeFromDT(event?.recorded_at)}
        </Body_14_Coral_Primary_Regular_14>
    )
}

const renderTime = (workday: WorkDayWithEvents, type: "in" | "out") => {
    return <WorkTime workday={workday} type={type}/>
}
export const eventsTableHeaderItems: Array<HeaderItem> = [
    {
        label: <Gt>Date</Gt>,
        dataLabel: "date",
        paramKey: 'date',
        type: TableHeaderAction.sorting,
        renderer: (date) =>
            <Body_14_Coral_Primary_Regular_14>
                <GtDatetime>{date}</GtDatetime>
            </Body_14_Coral_Primary_Regular_14>
    },
    {
        label: <Gt>Work time</Gt>,
        dataLabel: "checkin_time",
        paramKey: 'self',
        type: TableHeaderAction.sorting,
        renderer: (data) => renderTime(data, "in")
    },
    {
        label: <Gt>GPS in</Gt>,
        dataLabel: "checkin_coords",
        paramKey: 'self',
        type: TableHeaderAction.sorting,
        renderer: (data) => renderCoords(data, "in")
    },
    {
        label: <Gt>Leave work time</Gt>,
        dataLabel: "checkout_time",
        paramKey: 'self',
        type: TableHeaderAction.sorting,
        renderer: (data) => renderTime(data, "out")
    },
    {
        label: <Gt>GPS out</Gt>,
        dataLabel: "checkout_coords",
        paramKey: 'self',
        type: TableHeaderAction.sorting,
        renderer: (data) => renderCoords(data, "out")
    },
    {
        label: <Gt>Total working hours</Gt>,
        dataLabel: "working_time",
        paramKey: 'self',
        type: TableHeaderAction.sorting,
        renderer: (workday) => <WorkingTime workdays={[workday]}/>
    },
]

const TableRow = ({headerColumns, item, expandable}: {
    headerColumns: Array<HeaderItem>,
    item: any,
    expandable: boolean
}) => {
    const [showDetail, setShowDetail] = useState(false)
    if (!item) return null

    return (
        <>
            <div className={cn(styles.tableRow, {[styles.expanded]: showDetail, [styles.expandableRow]: expandable})}
                 onClick={() => expandable && setShowDetail(!showDetail)}>
                {headerColumns.map(column =>
                    <TableRowValue column={column} item={item} key={`${column.dataLabel}-${item?.id}`}/>
                )}
                {expandable &&
                    <div className={cn(styles.tableCell, styles.buttonsColl)} data-label={"chevron"}>
                        {
                            showDetail
                                ? <ArrowChevronUp size={24}/>
                                : <ArrowChevronDown size={24}/>
                        }
                    </div>
                }
            </div>
            {(showDetail && expandable) &&
                <div className={styles.detailRow}>
                    <div className={styles.detailInner}>
                        <DailyEventsTable headerColumns={eventsTableHeaderItems} content={item?.workdays}/>
                    </div>
                </div>
            }
        </>
    )
}

export const DailyEventsTable = ({headerColumns, content}: {
    headerColumns: Array<HeaderItem>,
    content?: WorkDayWithEvents[]
}) => {
    const cssVars = {"--cols": headerColumns.length} as React.CSSProperties & {
        ["--cols"]: number;
    };

    return (
        <div className={styles.eventsTable} style={cssVars}>
            <TableHeader headerColumns={headerColumns} expandable={false}/>
            <div className={styles.tableBody}>
                {content && content?.map(item =>
                    <TableRow headerColumns={headerColumns} item={item} key={item?.id} expandable={false}/>
                )}
            </div>
        </div>
    )
}

export const PeriodEventsTable = ({headerColumns, employees}: {
    headerColumns: Array<HeaderItem>,
    employees: EmployeeWithWorkdays[]
}) => {
    const cssVars = {"--cols": headerColumns.length} as React.CSSProperties & {
        ["--cols"]: number;
    };

    return (
        <div className={styles.eventsTable} style={cssVars}>
            <TableHeader headerColumns={headerColumns} expandable={true}/>

            <div className={cn(styles.tableBody, styles.expandable)}>
                {employees && employees?.map(item =>
                    <TableRow headerColumns={headerColumns} item={item} key={item.id} expandable={true}/>
                )}
            </div>

            {!employees?.length &&
                <div style={{backgroundColor: "white", padding: "16px"}}>
                    <DataNotFoundMessage text={<Gt>We couldn’t find users</Gt>}/>
                </div>
            }
        </div>
    )
}